// filepath: client/src/components/SEO/StructuredData.tsx
import React from 'react';

type StructuredDataType = 'organization' | 'software' | 'faq' | 'breadcrumb' | 'howto' | 'article' | 'contactpage';

interface StructuredDataProps {
  type: StructuredDataType;
  data?: any;
}

// Enhanced structured data schemas
const STRUCTURED_DATA_SCHEMAS: Record<StructuredDataType, any> = {
  organization: {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Flaha Agri Tech",
    "alternateName": "Flaha PA - Precision Agriculture Division",
    "url": "https://flahafast.flaha.org",
    "logo": {
      "@type": "ImageObject",
      "url": "https://flahafast.flaha.org/assets/logos/flaha_main_logo.svg",
      "width": 400,
      "height": 200
    },
    "description": "Professional hydroponic nutrient management solutions and precision agriculture technology for commercial operations",
    "foundingDate": "2024",
    "industry": "Agriculture Technology",
    "numberOfEmployees": "10-50",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "Global",
      "addressRegion": "Worldwide"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "email": "support@flaha.org",
        "availableLanguage": ["English"],
        "areaServed": "Worldwide"
      },
      {
        "@type": "ContactPoint",
        "contactType": "technical support",
        "email": "support@flaha.org",
        "availableLanguage": ["English"],
        "areaServed": "Worldwide"
      }
    ],
    "sameAs": [
      "https://www.linkedin.com/company/flaha-agri-tech"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Hydroponic Solutions",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "SoftwareApplication",
            "name": "FlahaFast Nutrient Calculator"
          }
        }
      ]
    }
  },

  software: {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "FlahaFast",
    "alternateName": "FlahaFast Hydroponic Nutrient Calculator",
    "applicationCategory": ["Agriculture Technology", "Productivity", "Business"],
    "operatingSystem": ["Web Browser", "Progressive Web App"],
    "description": "Professional hydroponic nutrient calculator and management system for precision agriculture with scientific accuracy",
    "url": "https://flahafast.flaha.org",
    "downloadUrl": "https://flahafast.flaha.org",
    "screenshot": [
      "https://flahafast.flaha.org/assets/images/screenshot-desktop.png",
      "https://flahafast.flaha.org/assets/images/screenshot-mobile.png"
    ],
    "softwareVersion": "2.0.0",
    "datePublished": "2024-01-01",
    "dateModified": "2025-01-11",
    "author": {
      "@type": "Organization",
      "name": "Flaha Agri Tech",
      "url": "https://flahafast.flaha.org"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Flaha Agri Tech",
      "logo": {
        "@type": "ImageObject",
        "url": "https://flahafast.flaha.org/assets/logos/flaha_main_logo.svg"
      }
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "priceValidUntil": "2025-12-31"
    },
    "featureList": [
      "Water Quality Analysis & Management",
      "Professional Crop Formulation Tools", 
      "Comprehensive Nutrient Salt Library",
      "Stock Solution Preparation Calculator",
      "Unified Calculation Engine",
      "pH Adjustment Calculations",
      "EC/TDS Monitoring",
      "Multi-part Nutrient Systems"
    ],
    "requirements": "Modern web browser with JavaScript enabled",
    "memoryRequirements": "512MB RAM",
    "storageRequirements": "50MB",
    "processorRequirements": "Any modern processor",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "150",
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": [
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "Professional Grower"
        },
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        },
        "reviewBody": "Excellent precision and scientific accuracy for commercial hydroponic operations."
      }
    ]
  },

  faq: {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How do I calculate nutrient solutions with FlahaFast?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "FlahaFast provides a comprehensive 5-step workflow: 1) Analyze water quality parameters (pH, EC, ions), 2) Select appropriate crop formulation from our professional database, 3) Choose compatible nutrient salts from our extensive library, 4) Configure stock solution preparation with precise dilution ratios, 5) Execute unified calculation with scientific accuracy for professional results."
        }
      },
      {
        "@type": "Question",
        "name": "What makes FlahaFast different from other hydroponic calculators?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "FlahaFast offers enterprise-grade precision with scientific accuracy, comprehensive water quality analysis, professional crop formulations based on research data, extensive nutrient salt compatibility checking, and a unified calculation engine designed for commercial hydroponic operations. Our system handles complex multi-part nutrient systems and provides detailed preparation instructions."
        }
      },
      {
        "@type": "Question",
        "name": "Is FlahaFast suitable for commercial hydroponic operations?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, FlahaFast is specifically designed for professional and commercial hydroponic operations. It provides the precision, scalability, and scientific accuracy required for large-scale nutrient management, with support for complex formulations and detailed preparation protocols."
        }
      },
      {
        "@type": "Question",
        "name": "What water quality parameters does FlahaFast analyze?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "FlahaFast analyzes comprehensive water quality parameters including pH, electrical conductivity (EC), total dissolved solids (TDS), and individual ion concentrations (Ca²⁺, Mg²⁺, K⁺, Na⁺, SO₄²⁻, Cl⁻, HCO₃⁻, NO₃⁻). This ensures optimal nutrient solution preparation based on your specific water source."
        }
      },
      {
        "@type": "Question",
        "name": "Can I use FlahaFast for different crop types?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely! FlahaFast includes professional formulations for vegetables, herbs, fruits, leafy greens, and specialty crops. Each formulation is based on scientific research and optimized for different growth stages and environmental conditions."
        }
      }
    ]
  },

  contactpage: {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "FlahaFast Support & Contact",
    "description": "Get professional support for FlahaFast hydroponic nutrient management system",
    "url": "https://flahafast.flaha.org/support",
    "mainEntity": {
      "@type": "Organization",
      "name": "Flaha Agri Tech",
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "contactType": "customer support",
          "email": "support@flaha.org",
          "availableLanguage": "English",
          "areaServed": "Worldwide"
        }
      ]
    }
  },

  breadcrumb: {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://flahafast.flaha.org"
      }
    ]
  },

  howto: {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Calculate Hydroponic Nutrients",
    "description": "Step-by-step guide to calculating professional hydroponic nutrient solutions",
    "step": [
      {
        "@type": "HowToStep",
        "name": "Analyze Water Quality",
        "text": "Test and input your water source parameters including pH, EC, and ion concentrations"
      },
      {
        "@type": "HowToStep",
        "name": "Select Crop Formulation",
        "text": "Choose the appropriate nutrient formulation for your crop type and growth stage"
      },
      {
        "@type": "HowToStep",
        "name": "Choose Nutrient Salts",
        "text": "Select compatible nutrient salts from the comprehensive library"
      },
      {
        "@type": "HowToStep",
        "name": "Configure Stock Solutions",
        "text": "Set up stock solution preparation with proper dilution ratios"
      },
      {
        "@type": "HowToStep",
        "name": "Calculate Final Recipe",
        "text": "Execute the unified calculation engine for precise nutrient amounts"
      }
    ]
  },

  article: {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Professional Hydroponic Nutrient Management",
    "description": "Comprehensive guide to professional hydroponic nutrient calculation and management",
    "author": {
      "@type": "Organization",
      "name": "Flaha Agri Tech"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Flaha Agri Tech",
      "logo": {
        "@type": "ImageObject",
        "url": "https://flahafast.flaha.org/assets/logos/flaha_main_logo.svg"
      }
    },
    "datePublished": "2024-01-01",
    "dateModified": "2025-01-11"
  }
};

const StructuredData: React.FC<StructuredDataProps> = ({ type, data }) => {
  const schema = data || STRUCTURED_DATA_SCHEMAS[type];
  
  if (!schema) return null;

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(schema, null, 0)
      }}
    />
  );
};

export default StructuredData;
